import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Session, User } from '@supabase/supabase-js';
import type { Branch, ProfileWithBranches } from '../types/database';
import { useNotification } from './NotificationContext';
import { fetchUserProfile } from '../services/supabase/profile.service';
import { updateLastUsedBranch } from '../services/supabase/profile.service';
import { isProfileWithBranches } from '../utils/typeGuards';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userProfile: ProfileWithBranches | null;
  currentBranch: Branch | null;
  availableBranches: Branch[];
  loading: boolean;
  branchesLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setCurrentBranch: (branch: Branch) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<ProfileWithBranches | null>(null);
  const [currentBranch, setCurrentBranchState] = useState<Branch | null>(null);
  const [availableBranches, setAvailableBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();

  const logout = async () => {
    try {
      // Clear all storage first
      localStorage.clear();
      sessionStorage.clear();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      showSuccess('Logged out successfully');
      setUser(null);
      setUserProfile(null);
      
      // Use navigate and force a page reload
      navigate('/login');
      window.location.reload();
      
    } catch (error: any) {
      console.error('Logout error:', error);
      showError(error.message || 'Failed to logout');
    }
  };

  const idleTimeout = 30 * 60 * 1000; // 30 minutes in ms
  let idleTimer: number = 0;

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      logout();
    }, idleTimeout);
  };

  useEffect(() => {
    window.addEventListener('mousemove', resetIdleTimer);
    window.addEventListener('mousedown', resetIdleTimer);
    window.addEventListener('keypress', resetIdleTimer);
    window.addEventListener('touchstart', resetIdleTimer);
    window.addEventListener('scroll', resetIdleTimer);

    resetIdleTimer();

    return () => {
      window.removeEventListener('mousemove', resetIdleTimer);
      window.removeEventListener('mousedown', resetIdleTimer);
      window.removeEventListener('keypress', resetIdleTimer);
      window.removeEventListener('touchstart', resetIdleTimer);
      window.removeEventListener('scroll', resetIdleTimer);
      clearTimeout(idleTimer);
    };
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Listen for storage changes to sync currentBranch across tabs
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'currentBranch' && event.newValue) {
        try {
          const storedBranch: Branch = JSON.parse(event.newValue);
          // Update only if different from current state
          if (!currentBranch || storedBranch.id !== currentBranch.id) {
            setCurrentBranchState(storedBranch);
          }
        } catch (error) {
          console.error('Error parsing currentBranch from localStorage:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [currentBranch]);

  const loadUserProfile = async (userId: string) => {
    try {
      setBranchesLoading(true);

      const profile = await fetchUserProfile(userId);
      
      if (!isProfileWithBranches(profile)) {
        throw new Error('Invalid profile data received');
      }

      setUserProfile(profile);

      const userRole = profile.role;
      if (!['staff', 'manager', 'admin', 'agent', 'superagent'].includes(userRole)) {
        throw new Error('User does not have a valid role');
      }

      const validBranches = profile.profile_branches
        .map(pb => pb.branch)
        .filter((branch): branch is Branch => branch !== null && branch.is_active);
      setAvailableBranches(validBranches);

      // Check if there's a stored branch from another tab
      const storedBranch = localStorage.getItem('currentBranch');
      if (storedBranch) {
        const parsedBranch: Branch = JSON.parse(storedBranch);
        if (validBranches.find(b => b.id === parsedBranch.id)) {
          setCurrentBranchState(parsedBranch);
          return;
        }
      }

      if (profile.last_used_branch && profile.last_used_branch.is_active) {
        setCurrentBranchState(profile.last_used_branch);
        localStorage.setItem('currentBranch', JSON.stringify(profile.last_used_branch));
      } else if (validBranches.length > 0) {
        setCurrentBranchState(validBranches[0]);
        localStorage.setItem('currentBranch', JSON.stringify(validBranches[0]));
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      showError('Failed to load user profile');
    } finally {
      setBranchesLoading(false);
    }
  };

  const updateCurrentBranch = async (branch: Branch) => {
    try {
      if (!user) return;
      await updateLastUsedBranch(user.id, branch.id);
      setCurrentBranchState(branch);
      // Sync the branch across tabs
      localStorage.setItem('currentBranch', JSON.stringify(branch));
    } catch (error) {
      console.error('Error updating current branch:', error);
      showError('Failed to update current branch');
    }
  };

  const signOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error signing out:', error);
      showError('Failed to sign out');
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        await loadUserProfile(data.user.id);
      }
    } catch (error: any) {
      console.error('Error signing in:', error.message);
      showError(error.message);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        userProfile,
        currentBranch,
        availableBranches,
        loading,
        branchesLoading,
        signIn,
        signOut,
        setCurrentBranch: updateCurrentBranch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
